<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
    <div class="login-wrapper">
        <hox-loading-layer v-if="isLoggingIn" theme="dark" size="large" />
        <div class="login-form-wrapper">
            <hogarth-ic-logo class="logo" />

            <Form direction="vertical" class="login-form" @submit.prevent>
                <FormItem>
                    <Input
                        id="login-username"
                        v-model="form.username"
                        prefix="ios-contact-outline"
                        type="text"
                        placeholder="Username / Email Address"
                        size="large"
                        data-testid="login-username"
                        @keydown.stop
                        @keyup.enter="formLogin"
                    />
                </FormItem>

                <FormItem v-if="form.showPassword">
                    <Input
                        id="login-password"
                        v-model="form.password"
                        prefix="ios-lock-outline"
                        type="password"
                        placeholder="Password"
                        size="large"
                        data-testid="login-password"
                        @keydown.stop
                        @keyup.enter="formLogin"
                    />
                    <a class="forgot-password" @click.prevent="forgotPassword">Forgot Password?</a>
                </FormItem>

                <FormItem class="sign-in-btn-wrapper">
                    <Button
                        type="primary"
                        size="large"
                        class="primary-green sign-in-btn"
                        data-testid="login-button"
                        @click="formLogin"
                        v-text="form.showPassword ? 'Sign In' : 'Next'"
                    />
                </FormItem>
            </Form>
        </div>
    </div>
</template>

<script lang="ts">
import { mapMutations } from "vuex";
import { getProvider } from "@/utils";
import bus from "@/bus";
import HogarthIcLogo from "@/components/HogarthIcLogo.vue";
import type { Err } from "@/types/types";
import type { LocationQueryValue } from "vue-router";
import { getLandingRoute } from "@/router";

export default {
    // eslint-disable-next-line vue/multi-word-component-names
    name: "Login",
    components: {
        HogarthIcLogo
    },

    emits: [],

    data() {
        return {
            form: {
                username: "",
                password: "",
                showPassword: false
            },
            isLoggingIn: false
        };
    },

    async mounted() {
        bus.$emit("percentLoadEvent", 0);
        this.$apollo.provider.defaultClient.resetStore();

        if (this.$route.query?.oneTimeLogin) {
            await this.autocompleteAndSubmitForm();
        }
    },

    methods: {
        ...mapMutations("auth", ["setIsAuthorized"]),

        forgotPassword() {
            if (this.form.username.length) {
                this.$Modal.confirm({
                    title: "Forgot Password",
                    content: `Request a new password for ${this.form.username}?`,
                    okText: "Yes",
                    cancelText: "No",
                    onOk: async () => {
                        try {
                            await this.$auth.forgotPassword(this.form.username);
                            this.$router.push({
                                name: "Reset Password",
                                params: { username: this.form.username }
                            });
                        } catch (e) {
                            this.$snackbar.authentication(e as Err);
                        }
                    }
                });
            } else {
                this.$snackbar.fieldRequired("Username must not be empty");
            }
        },

        isProviderLogin() {
            if (this.form.username.includes("@")) {
                const provider = getProvider(this.form.username);

                if (provider) {
                    this.$auth.providerLogin(provider);
                    return true;
                }
            }

            return false;
        },

        validateLogin() {
            if (this.form.username.length === 0) {
                this.$snackbar.fieldRequired("Username must not be empty");
                return false;
            }

            if (this.isProviderLogin()) {
                return false;
            }

            if (!this.form.showPassword) {
                this.form.showPassword = true;
                return false;
            }

            if (this.form.password.length === 0) {
                this.$snackbar.fieldRequired("Password must not be empty");
                return false;
            }

            return true;
        },

        async login(username: string, password: string) {
            this.isLoggingIn = true;

            try {
                const { nextStep } = await this.$auth.login(username, password);

                if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                    this.setIsAuthorized(false);
                    this.$router.push({ name: "Reset Password", query: { forceResetPassword: "true" } });
                } else {
                    this.setIsAuthorized(true);
                    this.$router.push(getLandingRoute(this.$route));
                }
            } catch (e) {
                this.$snackbar.error(
                    "Login Error",
                    (e as Error & { code: string }).code === "UserNotFoundException"
                        ? "Incorrect username or password."
                        : (e as Error).message
                );
            }

            this.isLoggingIn = false;
        },

        async formLogin() {
            if (!this.validateLogin()) {
                return;
            }

            await this.login(this.form.username, this.form.password);
        },

        async autocompleteAndSubmitForm() {
            try {
                const { username, code } = JSON.parse(this.$route.query.oneTimeLogin as LocationQueryValue as string);
                this.form.showPassword = true;
                this.form.username = username;
                this.form.password = code;

                await this.formLogin();
            } catch {
                // ignore the error and just display the regular login
                this.form.showPassword = false;
                this.form.username = "";
                this.form.password = "";
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.login {
    &-wrapper {
        background: transparent;
        text-align: center;
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 360px;
        margin: 0 auto;
        flex-direction: column;
        color: #ffffff;

        .forgot-password {
            float: right;
            color: $white;
        }

        .sign-in-btn {
            height: auto;
            margin-top: 25px;
            padding: 10px 50px;
            width: 100%;

            &-wrapper {
                margin-top: 24px;
            }
        }
    }

    &-form-wrapper {
        width: 100%;
    }

    &-form {
        width: 100%;
    }
}
</style>
